import React, { ReactElement, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Container } from '../../components/Animations'
import Radom, { IPaymentLink, Payment } from '../../state/Radom'
import { RADOM_COLORS, PAY_BASE_URL } from '../../util/Constants'
import { useParams, useNavigate } from 'react-router-dom'
import TimeText from '../../components/TimeText'
import BreadCrumbs from '../../components/Breadcrumbs'
import {
  ProductThumbnail, PaymentAssetIcon
} from '../../components/PaymentDetails'
import { ExternalLinkText } from '../Events'
import LinkExternal from '../../components/LinkExternal'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import Table from '../../components/Table'
import { Currencies } from '../../util/Currencies'
import { getMethod } from '../../util/Managed'
import Pagination from '../../components/Pagination'
import { trimZeros } from '../../util/Util'
import { Section, SubTitle, SectionContent, SectionItem } from '../../components/Shared'

const ViewPaymentLink = observer((): ReactElement => {
  const params = useParams()
  const [paymentLink, setPaymentLink] = useState<IPaymentLink>()
  const [Payments, setPayments] = useState<Payment[]>()
  const navigate = useNavigate()
  const pageSize = 20
  const [curPage, setCurPage] = useState(0)
  const [numPages, setNumPages] = useState(0)

  const onPageClick = (i: number): void => {
    Radom.listPaymentsByPaymentLinkId(params.paymentLinkId ?? '', pageSize, i * pageSize)
      .then(res => {
        setPayments(res.data)
        setCurPage(i)
      })
  }

  useEffect(() => {
    if (!params.paymentLinkId) return
    Radom.getPaymentLink(params.paymentLinkId).then(res => {
      setPaymentLink(res)
    }).catch(err => {
      console.error(err)
    })

    Radom.listPaymentsByPaymentLinkId(params.paymentLinkId, pageSize, curPage * pageSize).then(res => {
      setPayments(res.data)
      setNumPages(Math.ceil(res.total / pageSize))
    }).catch(err => {
      console.error(err)
    })
  }, [params.id])

  if (!paymentLink) {
    return <></>
  }

  return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <BreadCrumbs items={[
        { item: 'Payment Links', to: '/payment_links' },
        { item: paymentLink.id, to: '.' }
      ]} />
    </div>

    <Section>
      <SubTitle style={{ fontSize: 18 }}>
            Payment link details
      </SubTitle>

      <SectionContent>
        <SectionItem>
          <SubTitle>ID</SubTitle>
          <ExternalLinkText href={`${PAY_BASE_URL}/pay/${paymentLink.id}`} target='_blank'>
            {paymentLink.id}
            <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
          </ExternalLinkText>
        </SectionItem>

        <SectionItem>
          <SubTitle>Created at</SubTitle>
          <div>
            <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(paymentLink.createdAt)} />
          </div>
        </SectionItem>

        {
          paymentLink.products && paymentLink.products.length > 0 &&
                  <SectionItem>
                    <SubTitle>Products</SubTitle>
                    <div style={{ fontSize: 14 }}>
                      {paymentLink.products.map((item, i) =>
                        <ProductThumbnail key={i} p={item} />
                      )}
                    </div>
                  </SectionItem>
        }
      </SectionContent>
      <SectionItem>
        <SubTitle>Payment methods</SubTitle>
        <div key="paymentMethods" style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', padding: '10px 0' }}>
          {
            paymentLink.gateway.managed?.methods.map((method, i) =>
              <PaymentMethodDisplay
                key={i}
                method={method}
                excludeName
                disableTooltip={false} />)
          }
        </div>
      </SectionItem>
    </Section>

    {
      Payments && Payments.length > 0 &&
    <Section>
      <SubTitle style={{ fontSize: 18 }}>
      Payment details
      </SubTitle>

      <SectionItem>
        <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Payment transactions</SubTitle>
        <Table
          headers={[
            <span key='payment-id'>Payment ID</span>,
            <span key="total">Amount</span>,
            <span key="payment">Payment</span>,
            <span key="date">Date</span>
          ]}
          items={
            Payments.map(d => [
              <div key='1'>
                <ExternalLinkText
                  onClick={e => e.stopPropagation()}
                  target="_blank" rel="noreferrer"
                  href={`/payment/${d.paymentEventId}`}>
                  {d.paymentEventId}
                </ExternalLinkText>
              </div>,
              <div key="2" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {Currencies.find(c => c.ticker.toLowerCase() === (d.currency ?? 'USD').toLowerCase())?.icon}
                <span>{trimZeros(d.total?.toString() ?? '')}</span>
              </div>,
              <div key="3" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <PaymentAssetIcon payment={d} />
                <span>{trimZeros(d.amount.toString())}</span>
                {getMethod(d.network, d.token)?.ticker}
              </div>,
              <TimeText key="4" date={new Date(d.startedAt)} />
            ])
          }
          isLoading={false}
          onClickRow={(i) => {
            const id = Payments[i].paymentEventId
            navigate(`/payment/${id}`)
          }}
        />
      </SectionItem>
      <Pagination curPage={curPage} numPages={numPages} onPageClick={onPageClick} />
    </Section>
    }
  </Container>
})

export default ViewPaymentLink
